<template>
  <wizard
    first-step="personalDetails"
    class="wizard-request-data"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="selectChildren"
      :model="formData"
      :next-step="submitSelectedChildren"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.selectChildren.title }}</h2>
        <slot name="select-children-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <h3>{{ i18n.form.labels.childRegistrationItems }}</h3>

        <FormulateInput
          type="group"
          name="children"
          :help="i18n.form.help.childRegistrationForm"
          :add-label="i18n.form.addlabel.childRegistrationForm"
          :repeatable="true"
          validation="bail|min:1,length|max:5,length|unique:childRegistrationForm"
          :validation-messages="i18n.form.validations.childRegistrationItems"
          @repeatableRemoved="resetChangesPerChild"
        >
          <div class="row">
            <!-- workaround for losing focus on first input -->
            <FormulateInput
              type="hidden"
              value="none"
            />
            <!-- /workaround -->
            <FormulateInput
              type="text"
              name="name"
              :label="i18n.form.labels.childName"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.childName"
            />
          </div>
        </FormulateInput>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="requestData"
      :model="formData"
      next-step="review"
      previous-step="selectChildren"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.requestData.title }}</h2>
        <slot name="request-data-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="dataTypes"
          :options="{
            annualStatement: i18n.form.labels.requestDataTypeOptions.annualStatement,
            agreement: i18n.form.labels.requestDataTypeOptions.agreement,
            hoursOverview: i18n.form.labels.requestDataTypeOptions.hoursOverview,
          }"
          type="checkbox"
          :label="i18n.form.labels.requestDataType"
          validation="required"
          :validation-messages="i18n.form.validations.requestDataType"
        />
      </fieldset>

      <fieldset v-if="formData.requestData.dataTypes.includes('annualStatement')">
        <div class="mb-6">
          <slot name="request-data-annual-statement-notification" />
        </div>
        <FormulateInput
          name="annualStatementYear"
          :options="annualStatementYearOptions"
          type="select"
          :label="i18n.form.labels.annualStatementYear"
          validation="required"
          :validation-messages="i18n.form.validations.annualStatementYear"
        />
      </fieldset>

      <fieldset v-if="formData.requestData.dataTypes.includes('hoursOverview')">
        <FormulateInput
          name="hoursOverviewYear"
          :options="hoursOverviewYearOptions"
          type="select"
          :label="i18n.form.labels.hoursOverviewYear"
          validation="required"
          :validation-messages="i18n.form.validations.hoursOverviewYear"
        />
      </fieldset>

      <fieldset>
        <FormulateInput
          name="remarkYesNo"
          :options="{
            yes: i18n.form.labels.remarkYesNoOptions.yes,
            no: i18n.form.labels.remarkYesNoOptions.no,
          }"
          type="radio"
          :label="i18n.form.labels.remarkYesNo"
          validation="required"
          :validation-messages="i18n.form.validations.remarkYesNo"
        />

        <FormulateInput
          v-if="formData.requestData.remarkYesNo === 'yes'"
          name="remark"
          type="textarea"
          validation="nohtml"
          :validation-messages="i18n.form.validations.remark"
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="requestData"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <th>{{ i18n.form.labels.reviewName }}</th>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewEmail }}</th>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewPhoneNumber }}</th>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewCustomerNumber }}</th>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>

          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>

          <template v-for="(child, childIndex) of resultingData.changesPerChild">
            <tr :key="child.childName + '-name'">
              <td>
                <span v-if="childIndex === 0">{{ i18n.form.labels.reviewChildren }}</span>
                <span v-if="childIndex !== 0">&nbsp;</span>
              </td>
              <td>
                {{ child.childName }}
              </td>
            </tr>
          </template>

          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>

          <template v-for="(dataType, dataTypeIndex) of resultingData.requestData.dataTypes">
            <tr :key="dataTypeIndex">
              <td>
                <span v-if="dataTypeIndex === 0">{{ i18n.form.labels.reviewDataTypes }}</span>
                <span v-if="dataTypeIndex !== 0">&nbsp;</span>
              </td>
              <td>
                {{ translateDataType(dataType) }}
                <span v-if="dataType === 'annualStatement'">({{ resultingData.requestData.annualStatementYear }})</span>
                <span v-if="dataType === 'hoursOverview'">({{ resultingData.requestData.hoursOverviewYear }})</span>
              </td>
            </tr>
          </template>

          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>

          <tr>
            <th>{{ i18n.form.labels.reviewRemark }}</th>
            <td>{{ resultingData.requestData.remark ? resultingData.requestData.remark : '' }}</td>
          </tr>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
  import Wizard from '@/components/wizards/components/Wizard.vue';
  import WizardStep from '@/components/wizards/components/WizardStep.vue';
  import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
  import axios from 'axios';
  import moment from 'moment';
  import getQueryParams from '@/lib/formulate/getFormQueryParam';

  export default {
    name: 'KfWizardRequestData',
    components: {
      Wizard,
      WizardStep,
      WizardStepIntro,
    },
    props: {
      i18n: {
        type: Object,
        default: null,
      },
      prefillData: {
        type: Object,
        default: () => { },
      },
      locale: {
        type: String,
        default: () => 'nl',
      },
      label: {
        type: String,
        default: 'Partou',
      },
      successPageUrl: {
        type: String,
        default: () => '/',
      },
      formToken: {
        type: String,
        default: () => '',
      },
    },
    data() {
      return {
        formData: {
          personalDetails: this.$store.state.wizard.personalDetails,
          selectChildren: this.$store.state.wizard.selectedChildren,
          requestData: {
            dataTypes: [],
          },
        },
        request: {
          isSending: false,
          errorSending: false,
        },
      };
    },
    computed: {
      annualStatementYearOptions() {
        const firstOfMarch = moment('0301', 'MMDD');
        const statementOfPastYearAvailableYet = moment().isSameOrAfter(firstOfMarch, 'day');
        const maxYear = statementOfPastYearAvailableYet ? moment().year() - 1 : moment().year() - 2;
        const minYear = moment().year() - 8;
        const options = {};

        for (let i = minYear; i < maxYear + 1; i++) {
          options[i] = i;
        }

        return options;
      },
      hoursOverviewYearOptions() {
        const currentYear = moment().year();
        return {
          [currentYear - 1]: currentYear - 1,
          [currentYear]: currentYear,
          [currentYear + 1]: currentYear + 1,
        };
      },
      resultingData() {
        return {
          label: this.label,
          locale: this.locale,
          formToken: this.formToken,
          personalDetails: this.formData.personalDetails,
          changesPerChild: this.formData.selectChildren.children
            .map((child) => {
              return {
                childName: child.name,
              };
            }),
          requestData: this.formData.requestData,
        };
      },
    },
    created() {
      this.formData.personalDetails = {
        ...this.formData.personalDetails,
        ...getQueryParams(this.prefillData)
      }
    },
    methods: {
      translateDataType(dataType) {
        return this.i18n.form.labels.requestDataTypeOptions[dataType];
      },
      submitPersonalDetails() {
        this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
        return 'selectChildren';
      },
      submitSelectedChildren() {
        this.$store.dispatch('wizard/setSelectedChildren', this.formData.selectChildren);
        return 'requestData';
      },
      resetChangesPerChild() {
        Object.keys(this.formData)
          .filter((k) => k.startsWith('changesForChild-'))
          .forEach((k) => (this.formData[k] = {}));
      },
      sendRequest() {
        this.request.errorSending = false;
        this.request.isSending = true;

        const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

        const config = {
          headers: {
            'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
          }
        };

        axios
          .post('/umbraco/api/wizard/requestdata', this.resultingData, config)
          .then(() => {
            window.location.href = this.successPageUrl;
          })
          .catch(() => {
            this.request.isSending = false;
            this.request.errorSending = true;
          });
      },
    },
  };
</script>
